import { Helmet } from 'react-helmet';
import {
  ButtonExport,
  Container, Scroll, StyledLink, Tooltip, TruncatedTextWrapper,
} from './styles';
import { AdminLayout } from '../AdminLayout';
import { FaultsLayout } from '../FaultsLayout';

import { t } from 'i18next';
import { HealthIcon, Loader, NewTable } from '~/components';
import { FilterFaults } from '~/components/FilterFaults';
import { useRef, useState } from 'react';
import { apiCall, apiCallDownload } from '~/providers';
import { useStateVar } from '~/helpers/useStateVar';
import { LoaderContainer } from '../FaultsList/styles';
import { toast } from 'react-toastify';
import { healthLevelDesc } from '~/components/HealthIcon';
import { createPortal } from 'react-dom';
import moment, { Moment } from 'moment-timezone';
import { LayoutIcon } from '@radix-ui/react-icons';
import { colors } from '~/styles/colors';
import { OpenEyeIcon, SearchIconFilters } from '~/icons';
import { FeedbackModal } from '~/pages/Analysis/DEVs/DevHealth/FeedbackModal';
import { formatPhone } from '~/helpers/formatPhone';
import { Flex } from 'reflexbox';
import { checkListParams } from '~/helpers/listParams';

interface Reports {
    ID: number;
    ASSET_HEALTH_HIST_ID: number;
    REASON: string;
    USER_NAME: string;
    PHONE_NUMBER?: string;
    MAINTENANCE_DONE?: boolean;
    MAINTENANCE_DATE?: string;
    CREATED_AT: string;
    UNIT_NAME: string;
    CLIENT_NAME: string;
    DEVICE_CODE: string;
    IMAGES?: string[];
}

type ImagePreview = {
  file: Blob;
  preview: string;
};

export const ContestedReports = (): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [maintenance, setMaintenance] = useState(false);
  const [images, setImages] = useState<ImagePreview[]>([]);

  const [state, render, setState] = useStateVar({
    feedbackInfo: {} as {
      ID: number;
      ASSET_HEALTH_HIST_ID: number;
      REASON: string;
      USER_NAME: string;
      PHONE_NUMBER?: string;
      MAINTENANCE_DONE?: number;
      MAINTENANCE_DATE?: string;
      ASSET_ID: number;
      IMAGES?: string[];
    },
    selectedDate: null as Moment | null,
    reason: null as string | null,
    phone: null as string | null,
    isLoadingModal: false,
    isLoading: false,
    reports: [] as Reports[],
    selectedClient: [] as number[],
    selectedUnit: [] as number[],
    selectedDevices: [] as string[],
    selectedHealthIndexes: [] as number[],
    selectedUsers: [] as string[],
    selectedPhone: '',
    selectedContestation: null as Moment | null,
    selectedMaintenance: [] as number[],
    selectedMaintenanceDate: null as Moment | null,
  });
  const TABLE_COLUMNS = [
    {
      name: 'client',
      value: t('cliente'),
      accessor: 'CLIENT_NAME',
      render: (props) => ((props.CLIENT_NAME) ? props.CLIENT_NAME : '-'),
    },
    {
      name: 'unit',
      value: t('unidade'),
      accessor: 'UNIT_NAME',
      render: (props) => ((props.UNIT_NAME) ? <StyledLink to={`/analise/unidades/${props.UNIT_ID}`}>{props.UNIT_NAME || '-'}</StyledLink> : (props.UNIT_NAME || '-')),
    },
    {
      name: 'user',
      value: t('usuario'),
      accessor: 'USERNAME',
      render: (props) => ((props.USER_NAME) ? props.USER_NAME : '-'),
    },
    {
      name: 'phone',
      value: t('telefone'),
      accessor: 'PHONE_NUMBER',
      render: (props) => ((props.PHONE_NUMBER) ? props.PHONE_NUMBER : '-'),
    },
    {
      name: 'created_at',
      value: t('dataDaContestacao'),
      accessor: 'CREATED_AT',
      render: (props) => {
        if (!props.CREATED_AT) return '-';
        const date = new Date(props.CREATED_AT);
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      name: 'device',
      value: t('dispositivo'),
      accessor: 'DEVICE_CODE',
      render: (props) => (<StyledLink to={`/analise/dispositivo/${props.DEVICE_CODE}/saude`}>{props.DEVICE_CODE}</StyledLink>),
    },
    {
      name: 'status',
      value: t('status'),
      accessor: 'H_INDEX',
      render: (props) => <HealthIcon health={(props.H_INDEX || 0).toString()} label={healthLevelDesc[String(props.H_INDEX)]} />,
    },
    {
      name: 'reason',
      value: t('motivo'),
      accessor: 'REASON',
      render: (props) => (
        props.REASON
          ? <TruncatedText text={props.REASON} maxLength={20} />
          : '-'
      ),
    },
    {
      name: 'maintenance_done',
      value: t('manutencaoRealizada'),
      accessor: 'MAINTENANCE_DONE',
      render: (props) => {
        if (props.MAINTENANCE_DONE === 1) return t('sim');
        return t('nao');
      },
    },
    {
      name: 'maintenance_date',
      value: t('dataDaManutencao'),
      accessor: 'MAINTENANCE_DATE',
      render: (props) => {
        if (!props.MAINTENANCE_DATE) return '-';
        const date = new Date(props.MAINTENANCE_DATE);
        return date.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      name: 'images',
      value: t('Imagens'),
      accessor: 'TOTAL_IMAGES',
      render: (props) => ((props.TOTAL_IMAGES) ? `${props.TOTAL_IMAGES} ${t('imagens')}` : '-'),
    },
    {
      name: 'action',
      value: t('acoes'),
      accessor: 'ID',
      render: (props) => (
        <button
          type="button"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
          onClick={() => {
            handleModalOpen(props.ID, props.CLIENT_ID, props.UNIT_ID);
          }}
        >
          <OpenEyeIcon color={colors.Blue400} />
        </button>
      ),
    },
  ];

  const handleModalClose = () => {
    setIsModalOpen(false);
    setImages([]);
    images.forEach((img) => URL.revokeObjectURL(img.preview));
    setMaintenance(false);
    setState({
      selectedDate: null,
      reason: null,
      phone: null,
    });
    render();
  };

  const handleModalOpen = (id, client_id, unit_id) => {
    getModal(id, client_id, unit_id);
    setIsModalOpen(true);
    render();
  };

  async function getModal(id: number, client_id: number, unit_id: number) {
    try {
      setState({ isLoadingModal: true });
      const feedback = await apiCall('/asset/get-maintainer-feedback', {
        id,
        client_id,
        unit_id,
      });
      setMaintenance(feedback.MAINTENANCE_DONE === 1);
      setState({
        reason: feedback.REASON || null,
        phone: feedback.PHONE_NUMBER || null,
        selectedDate: feedback.MAINTENANCE_DATE ? moment(feedback.MAINTENANCE_DATE) : null,
      });

      setImages((feedback.IMAGES || []).map((imgUrl) => ({
        file: new Blob(),
        preview: imgUrl,
      })));

      setState({ feedbackInfo: feedback, isLoadingModal: false });
    } catch (e) {

    }
  }

  async function loadData() {
    setState({ isLoading: true });
    try {
      const list = await apiCall('/asset/list-maintainer-feedback', {
        clientIds: checkListParams(state.selectedClient),
        unitIds: checkListParams(state.selectedUnit),
        deviceIds: checkListParams(state.selectedDevices),
        users: checkListParams(state.selectedUsers),
        phone: state.selectedPhone || undefined,
        statusHealth: checkListParams(state.selectedHealthIndexes),
        contestation: state.selectedContestation
          ? state.selectedContestation.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        maintenance: checkListParams(state.selectedMaintenance),
        maintenanceDate: state.selectedMaintenanceDate
          ? state.selectedMaintenanceDate.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      });
      setState({ reports: list });
      render();
    } catch (e) {
      toast.error(t('erroSaudeMaquinas'));
    }
    setState({ isLoading: false });
  }

  const updateCheckInformations = (topic: string, newValues?: [], dates?: Moment | null, option?: boolean, valueNumber?: string) => {
    switch (topic) {
      case 'client':
        setState({ selectedClient: newValues });
        break;
      case 'unit':
        setState({ selectedUnit: newValues });
        break;
      case 'users':
        setState({ selectedUsers: newValues });
        break;
      case 'phone':
        setState({ selectedPhone: valueNumber });
        break;
      case 'devices':
        setState({ selectedDevices: newValues });
        break;
      case 'contestation':
        setState({ selectedContestation: dates });
        break;
      case 'statusHealth':
        setState({ selectedHealthIndexes: newValues });
        break;
      case 'maintenance':
        setState({ selectedMaintenance: newValues });
        break;
      case 'maintenanceDate':
        setState({ selectedMaintenanceDate: dates });
        break;
    } };

  async function exportData() {
    toast.success(t('mensagemDeExportacaoIniciada'));
    try {
      const resp = await apiCallDownload('/export-maintainer-faults-feedback', {
        clientIds: checkListParams(state.selectedClient),
        unitIds: checkListParams(state.selectedUnit),
        deviceIds: checkListParams(state.selectedDevices),
        users: checkListParams(state.selectedUsers),
        phone: state.selectedPhone || undefined,
        statusHealth: checkListParams(state.selectedHealthIndexes),
        contestation: state.selectedContestation
          ? state.selectedContestation.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
        maintenance: checkListParams(state.selectedMaintenance),
        maintenanceDate: state.selectedMaintenanceDate
          ? state.selectedMaintenanceDate.format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      });
      if (resp.data.type === 'application/json') {
        const msg = await resp.data.text();
        throw new Error(msg);
      }

      const cd = resp.headers['content-disposition'];
      const fallback = `${t('falhas')}.xlsx`;
      const filenameHdr = resp.headers.filename as string | undefined;
      const filename = filenameHdr ?? fallback;

      const blob = resp.data;
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      toast.success(t('sucessoExportacao'));
    } catch (err) {
      console.error(err);
      toast.error(t('erroAoExportar'));
    }
  }

  return (
    <Container>
      <Helmet>
        <title>{t('Diel Energia - Falhas contestadas')}</title>
      </Helmet>
      <AdminLayout />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '23px' }}>
        <FaultsLayout />
      </div>
      <FilterFaults
        deviceType="dac"
        onAply={() => loadData()}
        state={state}
        updateInfos={updateCheckInformations}
        listFilters={['cliente', 'unidade', 'deviceId', 'users', 'phone', 'contestation', 'statusHealth', 'maintenance', 'maintenanceDate']}
      />
      {state.isLoading ? (
        <LoaderContainer>
          <Loader variant="primary" />
          <span>Carregando</span>
        </LoaderContainer>
      )
        : state.reports.length > 0 ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
              <h3>
                {state.reports.length}
                {' '}
                {t('laudo(s)Encontrado(s)')}
              </h3>
              <ButtonExport onClick={() => exportData()}>
                <LayoutIcon style={{ marginRight: '5px' }} />
                {t('exportarPlanilha')}
              </ButtonExport>
            </div>
            <Scroll style={{ overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap' }}>
              <NewTable
                noSearchBar
                data={state.reports}
                columns={TABLE_COLUMNS}
                pageSize={20}
                styledPagination
              />
            </Scroll>
          </>
        ) : (
          <>
            <Flex flexDirection="column" justifyContent="center" alignItems="center" marginTop="20px">
              <SearchIconFilters />
              <strong style={{ margin: '20px 0px' }}>
                {t('NenhumResultadoEncontrado')}
              </strong>
              <span style={{ color: colors.Grey500 }}>{t('TenteAjustarOsFiltrosOuReviseOsTermosDaBuscaParaEncontrarOQueProcura')}</span>
            </Flex>
            <div />
          </>
        )}
      {isModalOpen && (
      <FeedbackModal
        formatPhone={formatPhone}
        isLoading={state.isLoadingModal}
        state={state}
        setState={setState}
        maintenance={maintenance}
        setMaintenance={setMaintenance}
        images={images}
        handleModalClose={handleModalClose}
        render={render}
        handleImageChange={() => {}}
        removeImage={() => {}}
        submitFeedback={() => {}}
      />
      )}
    </Container>
  );
};

interface TruncatedTextProps {
  text: string;
  maxLength?: number;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxLength = 20 }) => {
  const [show, setShow] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const truncated = text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

  const handleMouseEnter = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 6, // tooltip abaixo do texto
      left: rect.left + rect.width / 5,
    });
    setShow(true);
  };

  return (
    <>
      <TruncatedTextWrapper ref={ref} onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)}>
        {truncated}
      </TruncatedTextWrapper>
      {show
        && createPortal(
          <Tooltip top={coords.top} left={coords.left}>
            {text}
          </Tooltip>,
          document.body,
        )}
    </>
  );
};
